import { PrimaryButton, RadioGroup, Space, TertiaryButton } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type FinancialAccountTypeEnum } from '@webapp/platform/banking/entities/enums/FinancialAccountTypeEnum'
import { type PaymentMethodCategoryType } from '@webapp/platform/banking/entities/enums/PaymentMethodCategoryEnum'
import messages from './messages'
import { SelectPaymentMethodCategoryRadioGroupSectionStyled } from './styles/SelectPaymentMethodCategoryRadioGroupSectionStyled'
import { SelectPaymentMethodCategoryStepFooterStyled } from './styles/SelectPaymentMethodCategoryStepFooterStyled'
import { SelectPaymentMethodCategoryStepStyled } from './styles/SelectPaymentMethodCategoryStepStyled'
import {
  type OnSubmitPayload as OnSubmitSelectPaymentMethodCategoryPayload,
  useSelectPaymentMethodCategoryStep,
} from '../../hooks/useSelectPaymentMethodCategoryStep'

export interface ISelectPaymentMethodCategoryStepProps {
  financialAccountType: Nullable<FinancialAccountTypeEnum>
  isFinancialAccountSetUp: boolean
  onSubmit: (data: OnSubmitSelectPaymentMethodCategoryPayload) => void
  onCancel: () => void
}

/**
 * Beneficiary form config step
 * The selected type determines the default values to use for the embedded beneficiary form
 */
export const SelectPaymentMethodCategoryStep = ({
  financialAccountType,
  isFinancialAccountSetUp,
  onSubmit,
  onCancel,
}: ISelectPaymentMethodCategoryStepProps) => {
  const intl = useProcurifyIntl()

  const {
    selectedPaymentMethod,
    paymentMethodCategoryOptions,
    handleMethodSelect,
    handleContinue,
    handleCancel,
    canContinue,
  } = useSelectPaymentMethodCategoryStep({
    isFinancialAccountSetUp,
    financialAccountType,
    onSubmit,
    onCancel,
  })

  return (
    <SelectPaymentMethodCategoryStepStyled>
      <SelectPaymentMethodCategoryRadioGroupSectionStyled>
        <RadioGroup
          name='beneficiary-type'
          value={selectedPaymentMethod ?? ''}
          onChange={(e) =>
            handleMethodSelect(e.target.value as PaymentMethodCategoryType)
          }
        >
          {paymentMethodCategoryOptions.map((option) => (
            <RadioGroup.CardItem
              key={option.type}
              value={option.type}
              label={option.title}
              description={option.description}
              infoBadgeText={option.titleLabel}
            />
          ))}
        </RadioGroup>
      </SelectPaymentMethodCategoryRadioGroupSectionStyled>

      <SelectPaymentMethodCategoryStepFooterStyled>
        <Space direction='vertical' justify='center' align='stretch'>
          <PrimaryButton
            onClick={handleContinue}
            disabled={!canContinue}
            fullWidth
          >
            {intl.formatMessage(messages.continueButton)}
          </PrimaryButton>
          <TertiaryButton onClick={handleCancel} fullWidth>
            {intl.formatMessage(messages.cancelButton)}
          </TertiaryButton>
        </Space>
      </SelectPaymentMethodCategoryStepFooterStyled>
    </SelectPaymentMethodCategoryStepStyled>
  )
}

SelectPaymentMethodCategoryStep.displayName = 'SelectPaymentMethodCategoryStep'
